import React, { useEffect } from "react";
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  View,
  SafeAreaView,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useFocusEffect } from "expo-router";
import Header from "@/components/ui/Header";
import { AppText } from "@/components/AppText";
import AdgemOfferCard from "@/components/adgem/OfferCard";
import { Offer } from "@/types/offer";
import { useOffers } from "@/hooks/useOffers";
import useWindow from "@/hooks/useWindow";
import useUserStore from "@/store/user";
import { getPlatform } from "@/utils/platform";
import EmptyState from "./components/EmptyState";
import LoadingState from "./components/LoadingState";
import ErrorState from "./components/ErrorState";
import FBTrackingService from "@/services/fbTrackingService";

const OfferWallScreen: React.FC = () => {
  const insets = useSafeAreaInsets();
  const { isDesktop } = useWindow();
  const { userProfile, getUserProfile } = useUserStore();

  const { offers, isLoading, isError, refetch, isRefetching } =
    useOffers();

  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("earn-rewards");
    }, [])
  );

  useEffect(() => {
    if (!userProfile) {
      getUserProfile();
    }
  }, [userProfile]);

  if (!userProfile) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Earn Rewards" />
        <View style={styles.errorContainer}>
          <AppText style={styles.errorText}>
            Please log in to view offers
          </AppText>
        </View>
      </SafeAreaView>
    );
  }

  if (isLoading && offers.length === 0) {
    return <LoadingState />;
  }

  if (isError && offers.length === 0) {
    return <ErrorState onRetry={refetch} />;
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Earn Rewards" />
      <View
        style={isDesktop ? styles.listContainerDesktop : styles.listContainer}
      >
        <FlatList
          data={offers}
          renderItem={({ item }: { item: Offer }) => (
            <AdgemOfferCard offer={item} />
          )}
          keyExtractor={(item) => item.id.toString()}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            styles.contentContainer,
            { paddingBottom: insets.bottom + 20 },
          ]}
          ListEmptyComponent={!isLoading && !isError ? <EmptyState /> : null}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={refetch}
              colors={["#4F46E5"]}
              tintColor="#4F46E5"
            />
          }
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          windowSize={10}
          removeClippedSubviews={true}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F8FAFC",
  },
  listContainerDesktop: {
    flex: 1,
    flexDirection: "row",
    alignSelf: "center",
  },
  listContainer: {
    flex: 1,
  },
  contentContainer: {
    maxWidth: 600,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    fontSize: 16,
    color: "#666",
  },
});

export default OfferWallScreen;
